import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ImageIcon, Upload, Ban, Droplets, Loader2 } from "lucide-react";
import { useLocalParticipant } from "@livekit/components-react";
import { Track } from "livekit-client";
import { BackgroundProcessor, supportsBackgroundProcessors } from "@livekit/track-processors";

const PRESETS = [
  "temple", "temple-stars", "temple-red", "temple-light", "anteroom",
  "office-green", "office-study", "office-modern", "library", "navy",
].map((id) => ({ id, src: `/backgrounds/${id}.jpg`, thumb: `/backgrounds/${id}.thumb.jpg` }));
const LS_KEY = "sc_meeting_bg";      // {mode, imagePath}
const LS_CUSTOM = "sc_meeting_bg_custom"; // data URL of the uploaded photo

/** Teams/Zoom-style camera background: none / blur / preset / own photo. */
export function BackgroundPicker() {
  const { t } = useTranslation();
  const { localParticipant, cameraTrack } = useLocalParticipant();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(() => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "null") || { mode: "disabled" }; } catch { return { mode: "disabled" }; } });
  const [custom, setCustom] = useState(() => localStorage.getItem(LS_CUSTOM) || null);
  const processorRef = useRef(null);
  const fileRef = useRef(null);
  const supported = supportsBackgroundProcessors();

  const apply = async (opt) => {
    const track = cameraTrack?.track || localParticipant?.getTrackPublication(Track.Source.Camera)?.track;
    if (!track) return;
    setBusy(true);
    try {
      if (!processorRef.current) {
        processorRef.current = BackgroundProcessor({ mode: "disabled" });
        await track.setProcessor(processorRef.current);
      } else if (track.getProcessor?.() !== processorRef.current) {
        await track.setProcessor(processorRef.current);
      }
      await processorRef.current.switchTo(opt);
      setCurrent(opt);
      localStorage.setItem(LS_KEY, JSON.stringify(opt));
    } catch (e) {
      console.warn("background", e);
    } finally { setBusy(false); }
  };

  // Re-apply the saved choice whenever the camera track (re)appears.
  const trackSid = cameraTrack?.publication?.trackSid;
  useEffect(() => {
    if (!supported || !trackSid || current.mode === "disabled") return;
    apply(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackSid]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const img = new Image();
    const url = URL.createObjectURL(f);
    img.onload = () => {
      // Downscale to ≤1280px so it fits comfortably in localStorage.
      const scale = Math.min(1, 1280 / img.width);
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      const dataUrl = c.toDataURL("image/jpeg", 0.82);
      URL.revokeObjectURL(url);
      try { localStorage.setItem(LS_CUSTOM, dataUrl); } catch { /* too big → session only */ }
      setCustom(dataUrl);
      apply({ mode: "virtual-background", imagePath: dataUrl });
    };
    img.src = url;
    e.target.value = "";
  };

  if (!supported) return null;
  const isActive = (opt) => current.mode === opt.mode && (opt.mode !== "virtual-background" || current.imagePath === opt.imagePath);
  const tile = (key, opt, label, thumb) => (
    <button key={key} type="button" onClick={() => apply(opt)} disabled={busy} title={label} data-testid={`bg-option-${key}`}
      style={{ width: 96, height: 64, borderRadius: 8, overflow: "hidden", border: isActive(opt) ? "2px solid #c9a84c" : "2px solid transparent", background: "#16283a", color: "#e8d59a", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", padding: 0 }}>
      {thumb ? <img src={thumb} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : label}
    </button>
  );

  return (
    <div style={{ position: "relative" }} data-testid="bg-picker">
      <button type="button" className="lk-button" onClick={() => setOpen((o) => !o)} data-testid="bg-picker-toggle" aria-pressed={current.mode !== "disabled"}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />} {t("meetingsPage.background", "Fundal")}
      </button>
      {open ? (
        <div data-testid="bg-picker-panel" style={{ position: "absolute", bottom: "calc(100% + 10px)", left: "50%", transform: "translateX(-50%)", background: "#0d1b2a", border: "1px solid #c9a84c", borderRadius: 12, padding: 12, display: "flex", gap: 8, flexWrap: "wrap", width: 444, maxHeight: 340, overflowY: "auto", zIndex: 20, boxShadow: "0 12px 30px rgba(0,0,0,0.4)" }}>
          {tile("none", { mode: "disabled" }, <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}><Ban size={14} /> {t("meetingsPage.bgNone", "Fără")}</span>)}
          {tile("blur", { mode: "background-blur", blurRadius: 12 }, <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}><Droplets size={14} /> {t("meetingsPage.bgBlur", "Estompat")}</span>)}
          {PRESETS.map((p) => tile(p.id, { mode: "virtual-background", imagePath: p.src }, p.id, p.thumb))}
          {custom ? tile("custom", { mode: "virtual-background", imagePath: custom }, "custom", custom) : null}
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="bg-upload-btn"
            style={{ width: 96, height: 64, borderRadius: 8, border: "1px dashed #c9a84c", background: "transparent", color: "#e8d59a", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, fontSize: 11 }}>
            <Upload size={14} /> {t("meetingsPage.bgUpload", "Poza mea")}
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} data-testid="bg-upload-input" />
        </div>
      ) : null}
    </div>
  );
}
